import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { Stack, Row, Text, Button, Tag } from './index';

interface Props {
  artifact: any;
  onClose?: () => void;
}

const STATUS_COLOR: Record<string, string> = {
  green: 'text-positive',
  yellow: 'text-amber-500',
  red: 'text-negative',
  pending: 'text-muted',
  error: 'text-negative',
};

const SEVERITY_DOT: Record<string, string> = {
  high: 'bg-negative',
  medium: 'bg-amber-400',
  low: 'bg-muted',
};

export function ArtifactView({ artifact: art, onClose }: Props) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ created: number; linked: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const findings = art?.reviewFindings as any;
  const entities: any[] = findings?.entities ?? [];
  const flags: any[] = findings?.flags ?? [];
  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(entities.map((e: any, i: number) => [i, true]))
  );

  const isKyc = art?.type === 'kyc' || art?.type === 'kyc-report';
  const isReviewed = ['green', 'yellow', 'red'].includes(art?.reviewStatus ?? '');
  const canMaterialize = isKyc && isReviewed && entities.length > 0;
  const alreadyDone = result !== null;

  async function materialize() {
    setBusy(true); setErr(null);
    try {
      const r = await api.materializeEntitiesFromKyc(art.id);
      setResult({ created: r.created, linked: r.linked });
    } catch (e: any) {
      setErr(e.message);
    } finally { setBusy(false); }
  }

  return (
    <Stack gap={3}>
      <Row justify="between" align="start">
        <Stack gap={0.5}>
          <Text variant="small" className="font-semibold">{art.title}</Text>
          <Row gap={1}>
            <Tag>{art.type}</Tag>
            {art.producedByAgent && <Text variant="label">by {art.producedByAgent} agent</Text>}
            {!art.producedByAgent && <Text variant="label">uploaded</Text>}
          </Row>
        </Stack>
        {onClose && <button onClick={onClose} className="text-muted hover:text-ink text-xs">close</button>}
      </Row>

      {art.reviewStatus && (
        <Stack gap={1}>
          <Row gap={1} align="center">
            <span className={`h-2 w-2 rounded-full ${art.reviewStatus === 'green' ? 'bg-positive' : art.reviewStatus === 'red' || art.reviewStatus === 'error' ? 'bg-negative' : art.reviewStatus === 'yellow' ? 'bg-amber-400' : 'bg-muted'} shrink-0`} />
            <Text variant="small" className={STATUS_COLOR[art.reviewStatus] ?? 'text-muted'}>
              {art.reviewStatus === 'pending' ? 'review in progress…' : `review ${art.reviewStatus}`}
              {art.reviewSummary ? ` — ${art.reviewSummary}` : ''}
            </Text>
          </Row>
          {flags.length > 0 && (
            <Stack gap={0.5}>
              {flags.slice(0, 6).map((f: any, i: number) => (
                <Row key={i} gap={1} align="start">
                  <span className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${SEVERITY_DOT[f.severity] ?? 'bg-muted'}`} />
                  <Text variant="label">{f.category}: {f.description}</Text>
                </Row>
              ))}
              {flags.length > 6 && <Text variant="label" muted>+{flags.length - 6} more flags</Text>}
            </Stack>
          )}
        </Stack>
      )}

      {canMaterialize && (
        <Stack gap={2} className="hairline rounded-sm p-3 bg-whisper">
          <Text variant="micro">Extracted entities — create CRM profiles?</Text>
          <Stack gap={1}>
            {entities.map((ent: any, i: number) => (
              <Row key={i} gap={1} align="center">
                {!alreadyDone && (
                  <input
                    type="checkbox"
                    checked={checked[i] ?? true}
                    onChange={e => setChecked(c => ({ ...c, [i]: e.target.checked }))}
                    className="mt-0.5"
                  />
                )}
                {alreadyDone && <span className="text-positive text-xs">✓</span>}
                <Text variant="small">{ent.name}</Text>
                <Tag>{ent.type ?? 'company'}</Tag>
                {ent.jurisdiction && <Text variant="label">{ent.jurisdiction}</Text>}
              </Row>
            ))}
          </Stack>
          {err && <Text variant="label" className="text-negative">{err}</Text>}
          {alreadyDone ? (
            <Row gap={2} align="center">
              <span className="text-positive text-sm">✓ Profiles created ({result.created} new)</span>
              <Link to="/crm" className="text-xs text-accent hover:underline">View in CRM →</Link>
            </Row>
          ) : (
            <Button variant="primary" size="sm" disabled={busy} onClick={materialize}>
              {busy ? 'creating…' : `Create ${entities.filter((_: any, i: number) => checked[i] !== false).length} CRM profile${entities.length !== 1 ? 's' : ''}`}
            </Button>
          )}
        </Stack>
      )}

      <Stack gap={1}>
        {art.dealId && <Text variant="label">deal: {art.dealId.slice(0, 12)}…</Text>}
        <Text variant="label">v{art.version} · {new Date(art.createdAt).toLocaleDateString()}</Text>
        <a
          href={`${import.meta.env.VITE_API_URL || 'https://api.ardacious.app'}/vault/${art.id}/download`}
          className="text-xs text-accent hover:underline"
        >download →</a>
      </Stack>
    </Stack>
  );
}
